import { Alert, Box, Stack, Text, useMantineTheme } from '@mantine/core'
import { GeoJson, GeoJsonFeature } from 'pigeon-maps'
import { useCallback, useState } from 'react'
import { TbAlertTriangle } from 'react-icons/tb'
import { useTranslation } from 'react-i18next'

import MapViewer from '@/components/MapViewer'
import { toPragueLocaleString } from '@/utils/datetime'
import { useAnnexationsForToday } from '@/hooks/useAnnexationsForToday'
import { AnnexationRow } from '@/api/main/types.gen'

import { EventLocation } from './eventLocation'

type Props = {
  location?: EventLocation
  height?: number
}

const AnnexationsPanel = ({ location, height = 320 }: Props) => {
  const { t } = useTranslation('unexpectedEvents')
  const theme = useMantineTheme()
  const { data, isLoading, isError, displayEnabled } = useAnnexationsForToday()
  const [selected, setSelected] = useState<AnnexationRow | null>(null)

  const handleFeatureClick = useCallback(
    (annexation: AnnexationRow) => (e: { event: MouseEvent }) => {
      e.event.stopPropagation()
      setSelected(prev => (prev?.section === annexation.section ? null : annexation))
    },
    []
  )

  if (!displayEnabled) return null

  return (
    <Stack gap="xs">
      {!location && (
        <Alert color="gray" variant="light">
          {t('header.noGps')}
        </Alert>
      )}

      {location && isLoading && (
        <Text size="sm" c="dimmed">
          {t('panels.annexationsLoading')}
        </Text>
      )}

      {location && isError && (
        <Alert color="red" icon={<TbAlertTriangle />}>
          {t('panels.annexationsLoadError')}
        </Alert>
      )}

      {location && data && (
        <>
          <MapViewer
            sx={{ height }}
            center={{ longitude: location.longitude, latitude: location.latitude }}
            zoom={17}
            overlays={[
              <GeoJson key="annexations">
                {data.annexations.map((annexation, index) => {
                  const isSelected = selected?.section === annexation.section
                  return (
                    <GeoJsonFeature
                      key={`${annexation.section}-${index}`}
                      feature={{
                        type: 'Feature',
                        geometry: annexation.geometry as unknown as GeoJSON.Geometry,
                        properties: {},
                      }}
                      svgAttributes={{
                        strokeWidth: 3,
                        stroke: isSelected ? theme.colors.blue[7] : theme.colors.red[7],
                        fill: isSelected ? theme.colors.blue[3] : theme.colors.red[3],
                        fillOpacity: 0.25,
                        cursor: 'pointer',
                      }}
                      onClick={handleFeatureClick(annexation)}
                    />
                  )
                })}
              </GeoJson>,
            ]}
          />

          <Box>
            {!selected ? (
              <Text size="sm" c="dimmed">
                {t('panels.annexationSelectHint')}
              </Text>
            ) : (
              <Stack gap={2}>
                <Text fw={500}>{t('panels.annexationSection', { section: selected.section })}</Text>
                <Text size="sm">
                  {t('panels.annexationStart')}: {toPragueLocaleString(new Date(selected.start))}
                </Text>
                <Text size="sm">
                  {t('panels.annexationStop')}: {toPragueLocaleString(new Date(selected.end))}
                </Text>
                <Text size="sm" c="dimmed">
                  {selected.description}
                </Text>
              </Stack>
            )}
          </Box>
        </>
      )}
    </Stack>
  )
}

export default AnnexationsPanel
